import {
  IProductResSchema,
  IProductSchema,
  ProductSchema,
  ProductTypeEnum,
  ProrationPolicyEnum,
} from '@monetize/types/app';
import pick from 'lodash/pick';
import { z } from 'zod';
import { buildMergePatch } from '../../../utils/mergePatch';

export const ProductFormSchema = ProductSchema.extend({
  recognitionMethod: z.string().nullish(),
});

export type ProductFormValues = Omit<IProductSchema, 'recognitionMethod'> & {
  recognitionMethod?: string | null;
};

const PRODUCT_SCHEMA_KEYS = Object.keys(ProductSchema.shape);

/**
 * Maps a product response onto the flat shape the form edits - the response
 * nests usage types and the bucket, while the form holds their ids.
 */
export const getProductFormValues = (
  product: IProductResSchema,
): ProductFormValues => ({
  ...(pick(product, PRODUCT_SCHEMA_KEYS) as ProductFormValues),
  usageTypeIds: product.usageTypes
    ? product.usageTypes.map(({ id }) => id)
    : null,
  oneTimePrepaidCreditConfig:
    product.productType === ProductTypeEnum.ONETIME_PREPAID_CREDIT
      ? product.oneTimePrepaidCreditConfig
      : null,
  bucketId: product.bucket?.id || null,
  // Mirrors the normalization onSubmit applies, so an untouched policy is never
  // patched. Only ADVANCE exposes the control, and ARREARS - proratable on the
  // backend but not creatable here - would otherwise have its stored policy
  // cleared by an edit to an unrelated field.
  prorationPolicy:
    product.productType === ProductTypeEnum.ADVANCE
      ? (product.prorationPolicy ?? ProrationPolicyEnum.PRORATED)
      : null,
});

/**
 * Diffs the submitted form values against the values the form was loaded with
 * so `PATCH /api/products/{id}` only carries what the user actually changed.
 *
 * `id` is never patchable, and `customFields` is merged key by key server-side,
 * so a removed field has to be sent explicitly as `null`.
 */
export const buildProductPatchPayload = (
  original: ProductFormValues,
  updated: ProductFormValues,
): Partial<ProductFormValues> => {
  const { id: _originalId, ...originalValues } = original;
  const { id: _updatedId, ...updatedValues } = updated;
  return buildMergePatch(originalValues, updatedValues, ['customFields']);
};
